import React, { useState } from 'react';
import VerticalNavigation from '@salesforce/design-system-react/components/vertical-navigation';
import Icon from '@salesforce/design-system-react/components/icon';

// Pages
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import AnalyticsSuite from './pages/AnalyticsSuite';
import KanbanBoard from './pages/KanbanBoard';
import AdvancedDataGrid from './pages/AdvancedDataGrid';
import CalendarScheduling from './pages/CalendarScheduling';
import TimelineActivityFeed from './pages/TimelineActivityFeed';
import MasterDetailView from './pages/MasterDetailView';
import InteractiveFormsWizard from './pages/InteractiveFormsWizard';
import RecordPageComposer from './pages/RecordPageComposer';

const pages = [
  { id: 'overview', label: 'Overview', icon: 'home', component: null },
  { id: 'executive-dashboard', label: 'Executive Dashboard', icon: 'dashboard', component: ExecutiveDashboard },
  { id: 'analytics-suite', label: 'Analytics Suite', icon: 'analytics', component: AnalyticsSuite },
  { id: 'kanban-board', label: 'Kanban Board', icon: 'kanban', component: KanbanBoard },
  { id: 'advanced-data-grid', label: 'Advanced Data Grid', icon: 'table', component: AdvancedDataGrid },
  { id: 'calendar-scheduling', label: 'Calendar & Scheduling', icon: 'event', component: CalendarScheduling },
  { id: 'timeline-activity', label: 'Timeline & Activity', icon: 'log_a_call', component: TimelineActivityFeed },
  { id: 'master-detail', label: 'Master-Detail View', icon: 'side_list', component: MasterDetailView },
  { id: 'forms-wizard', label: 'Forms & Wizards', icon: 'form', component: InteractiveFormsWizard },
  { id: 'record-page', label: 'Record Page Composer', icon: 'record', component: RecordPageComposer },
];

const navCategories = [
  {
    id: 'showcase-nav',
    label: 'Showcase Pages',
    items: pages.map(p => ({ id: p.id, label: p.label })),
  },
];

const OverviewPage = ({ onNavigate }) => (
  <div style={{ padding: '32px' }}>
    <div style={{ textAlign: 'center', marginBottom: '48px' }}>
      <h1 style={{ fontSize: '36px', fontWeight: '300', color: '#080707', marginBottom: '12px' }}>
        What's Really Possible
      </h1>
      <p style={{ fontSize: '16px', color: '#706e6b', maxWidth: '700px', margin: '0 auto', lineHeight: '1.6' }}>
        Explore rich, interactive UI patterns built with Salesforce Lightning Design System components.
        Each page demonstrates real capabilities — dashboards, data grids, drag-and-drop boards,
        analytics, calendars, and more.
      </p>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {pages.filter(p => p.id !== 'overview').map(page => (
        <div
          key={page.id}
          onClick={() => onNavigate(page.id)}
          style={{
            backgroundColor: 'white',
            border: '1px solid #dddbda',
            borderRadius: '8px',
            padding: '24px',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#0176d3'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(1,118,211,0.15)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#dddbda'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: '#0176d3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon category="utility" name={page.icon} size="small" style={{ fill: 'white' }} />
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#080707' }}>{page.label}</h3>
          </div>
          <p style={{ fontSize: '13px', color: '#706e6b', margin: 0, lineHeight: '1.5' }}>
            {getPageDescription(page.id)}
          </p>
        </div>
      ))}
    </div>
  </div>
);

function getPageDescription(id) {
  const descriptions = {
    'executive-dashboard': 'Full analytics dashboard with KPIs, revenue trends, pipeline charts, team performance radar, and AI-powered insights.',
    'analytics-suite': 'Every chart type — line, bar, doughnut, radar, scatter, bubble, polar area, heatmap, and gauge visualizations.',
    'kanban-board': 'Drag-and-drop pipeline board with cards, quick actions, sliding detail panels, and real-time column totals.',
    'advanced-data-grid': 'Sortable, filterable data table with inline editing, bulk actions, expandable rows, and multiple view modes.',
    'calendar-scheduling': 'Full calendar with month, week, and day views — color-coded events, drag scheduling, and event creation.',
    'timeline-activity': 'Vertical timeline with calls, emails, meetings, tasks, and stage changes — filterable and expandable.',
    'master-detail': 'Split-panel layout with searchable record list, tabbed detail view, and inline editing.',
    'forms-wizard': 'Multi-step wizard flow and dynamic form builder with validation, dependent fields, and all SLDS form patterns.',
    'record-page': 'A fully-composed Salesforce record page showing how individual components come together.',
  };
  return descriptions[id] || '';
}

const ShowcaseHub = () => {
  const [activePage, setActivePage] = useState('overview');

  const ActiveComponent = pages.find(p => p.id === activePage)?.component;

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar */}
      <div style={{
        width: '260px',
        minWidth: '260px',
        backgroundColor: 'white',
        borderRight: '1px solid #dddbda',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #dddbda' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', margin: 0, color: '#080707' }}>
            Showcase
          </h2>
          <p style={{ fontSize: '12px', color: '#706e6b', margin: '4px 0 0 0' }}>
            What's Really Possible
          </p>
        </div>
        <VerticalNavigation
          categories={navCategories}
          selectedId={activePage}
          onSelect={(e, data) => setActivePage(data.item.id)}
        />
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', backgroundColor: '#f3f3f3' }}>
        {activePage === 'overview'
          ? <OverviewPage onNavigate={setActivePage} />
          : ActiveComponent
            ? <ActiveComponent />
            : <div style={{ padding: '48px', textAlign: 'center', color: '#706e6b' }}>Page not found</div>
        }
      </div>
    </div>
  );
};

export default ShowcaseHub;
